import {
  IInventoryAdvancePaymentActions,
  IInventoryAdvancePaymentApproverActionPayload,
  IInventoryAdvancePaymentFinanceManagerActionPayload,
  IInventoryAdvancePaymentAPSpecialistActionPayload,
  IInventoryAdvancePaymentFetchActionPayload,
  IInventoryAdvancePaymentStateActionPayload,
  InventoryAdvancePaymentContext,
  IInventoryAdvancePaymentAttachmentActionPayload,
  IInventoryAdvancePaymentAddAttachmentActionPayload,
  IInventoryAdvancePaymentCommentFetchActionPayload,
  IInventoryAdvancePaymentCommentUpdateActionPayload,
  IInventoryAdvancePaymentOwnerActionPayload,
  IInventoryAdvancePaymentOwnerMultipleUpdateActionPayload,
  IInventoryAdvancePaymentCommentAttachmentAddActionPayload,
  IInventoryAdvancePaymentCommentAttachmentRemoveActionPayload
} from './actions.types';
import {
  IInventoryAdvancePaymentData,
  IMentionAttachmentCommentData
} from '@/models';
import { ITransition, Transition } from '@/models/transition';
import {
  IInventoryAdvancePaymentPaginatedListData,
  IInventoryAdvancePaymentExportFilterParams
} from '@/services/inventory-advance-payment';
import { IUserData } from '@vue-altoleap-libraries/vue-altoleap-accounts-lib/types/model/userData.types';

export const actions: IInventoryAdvancePaymentActions = {
  fetchInventoryAdvancePayments(
    context: InventoryAdvancePaymentContext,
    payload: IInventoryAdvancePaymentFetchActionPayload
  ): Promise<void> {
    const promise = this.$services.inventoryAdvancePayment.fetchInventoryAdvancePayments(
      payload
    );
    context.commit('SET_LAST_PROMISE', promise);
    return promise.then(
      (inventoryAdvancePayments: IInventoryAdvancePaymentPaginatedListData) => {
        if (promise == context.state.lastPromise) {
          context.commit(
            'SET_INVENTORY_ADVANCE_PAYMENTS',
            inventoryAdvancePayments.results
          );
          context.commit('SET_COUNT', inventoryAdvancePayments.count);

          context.commit('SET_LAST_PROMISE', null);
        }
      }
    );
  },

  fetchInventoryAdvancePayment(
    context: InventoryAdvancePaymentContext,
    payload: number
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .fetchInventoryAdvancePayment(payload)
      .then((inventoryAdvancePayment: IInventoryAdvancePaymentData) => {
        const index = context.state.inventoryAdvancePayments.findIndex(
          (v: IInventoryAdvancePaymentData) =>
            v.id === inventoryAdvancePayment.id
        );
        if (index === -1) {
          context.commit(
            'ADD_INVENTORY_ADVANCE_PAYMENT',
            inventoryAdvancePayment
          );
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit(
            'UPDATE_INVENTORY_ADVANCE_PAYMENT',
            inventoryAdvancePayment
          );
        }
      });
  },

  createInventoryAdvancePayment(
    context: InventoryAdvancePaymentContext,
    payload: IInventoryAdvancePaymentData
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .createInventoryAdvancePayment(payload)
      .then((inventoryAdvancePayment: IInventoryAdvancePaymentData) => {
        context.commit(
          'ADD_INVENTORY_ADVANCE_PAYMENT',
          inventoryAdvancePayment
        );
        context.commit('INCREMENT_COUNT');
      });
  },

  updateInventoryAdvancePayment(
    context: InventoryAdvancePaymentContext,
    payload: IInventoryAdvancePaymentData
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .updateInventoryAdvancePayment(payload)
      .then((inventoryAdvancePayment: IInventoryAdvancePaymentData) => {
        const index = context.state.inventoryAdvancePayments.findIndex(
          (v: IInventoryAdvancePaymentData) =>
            v.id === inventoryAdvancePayment.id
        );
        if (index === -1) {
          context.commit(
            'ADD_INVENTORY_ADVANCE_PAYMENT',
            inventoryAdvancePayment
          );
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit(
            'UPDATE_INVENTORY_ADVANCE_PAYMENT',
            inventoryAdvancePayment
          );
        }
      });
  },

  deleteInventoryAdvancePayment(
    context: InventoryAdvancePaymentContext,
    payload: number
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .deleteInventoryAdvancePayment(payload)
      .then(() => {
        context.commit('REMOVE_INVENTORY_ADVANCE_PAYMENT', payload);
        context.commit('DECREMENT_COUNT');
      });
  },

  deleteInventoryAdvancePaymentAttachment(
    context: InventoryAdvancePaymentContext,
    payload: IInventoryAdvancePaymentAttachmentActionPayload
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .deleteInventoryAdvancePaymentAttachment(
        payload.inventoryAdvancePaymentId,
        payload.attachmentId
      )
      .then(() => {
        const index = context.state.inventoryAdvancePayments.findIndex(
          (v: IInventoryAdvancePaymentData) =>
            v.id === payload.inventoryAdvancePaymentId
        );
        if (index !== -1) {
          context.commit('REMOVE_INVENTORY_ADVANCE_PAYMENT_ATTACHMENT', {
            id: payload.inventoryAdvancePaymentId,
            attachment: payload.attachmentId
          });
        }
      });
  },

  addInventoryAdvancePaymentAttachments(
    context: InventoryAdvancePaymentContext,
    payload: IInventoryAdvancePaymentAddAttachmentActionPayload
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .addInventoryAdvancePaymentAttachments(payload.id, payload.attachments)
      .then((inventoryAdvancePayment: IInventoryAdvancePaymentData) => {
        const index = context.state.inventoryAdvancePayments.findIndex(
          (v: IInventoryAdvancePaymentData) =>
            v.id === inventoryAdvancePayment.id
        );
        if (index === -1) {
          context.commit(
            'ADD_INVENTORY_ADVANCE_PAYMENT',
            inventoryAdvancePayment
          );
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit(
            'UPDATE_INVENTORY_ADVANCE_PAYMENT',
            inventoryAdvancePayment
          );
        }
      });
  },

  updateInventoryAdvancePaymentState(
    context: InventoryAdvancePaymentContext,
    payload: IInventoryAdvancePaymentStateActionPayload
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .updateInventoryAdvancePaymentState(payload)
      .then((inventoryAdvancePayment: IInventoryAdvancePaymentData) => {
        const index = context.state.inventoryAdvancePayments.findIndex(
          (v: IInventoryAdvancePaymentData) =>
            v.id === inventoryAdvancePayment.id
        );
        if (index === -1) {
          context.commit(
            'ADD_INVENTORY_ADVANCE_PAYMENT',
            inventoryAdvancePayment
          );
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit(
            'UPDATE_INVENTORY_ADVANCE_PAYMENT',
            inventoryAdvancePayment
          );
        }
      });
  },

  fetchCurrentAvailableAssignees(
    context: InventoryAdvancePaymentContext,
    payload: number
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .fetchCurrentAvailableAssignees(payload)
      .then((assignees: IUserData[]) => {
        context.commit(
          'SET_INVENTORY_ADVANCE_PAYMENT_CURRENT_ASSIGNEES',
          assignees
        );
      });
  },

  fetchInventoryAdvancePaymentFinanceManagers(
    context: InventoryAdvancePaymentContext,
    payload: number
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .fetchInventoryAdvancePaymentFinanceManagers(payload)
      .then((managers: IUserData[]) => {
        context.commit(
          'SET_INVENTORY_ADVANCE_PAYMENT_FINANCE_MANAGERS',
          managers
        );
      });
  },

  fetchInventoryAdvancePaymentAPSpecialists(
    context: InventoryAdvancePaymentContext,
    payload: number
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .fetchInventoryAdvancePaymentAPSpecialists(payload)
      .then((apSpecialists: IUserData[]) => {
        context.commit(
          'SET_INVENTORY_ADVANCE_PAYMENT_AP_SPECIALISTS',
          apSpecialists
        );
      });
  },

  assignInventoryAdvancePaymentFinanceManager(
    context: InventoryAdvancePaymentContext,
    payload: IInventoryAdvancePaymentFinanceManagerActionPayload
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .assignInventoryAdvancePaymentFinanceManager(
        payload.inventoryAdvancePaymentId,
        payload.financeManager
      )
      .then((inventoryAdvancePayment: IInventoryAdvancePaymentData) => {
        const index = context.state.inventoryAdvancePayments.findIndex(
          (v: IInventoryAdvancePaymentData) =>
            v.id === inventoryAdvancePayment.id
        );
        if (index === -1) {
          context.commit(
            'ADD_INVENTORY_ADVANCE_PAYMENT',
            inventoryAdvancePayment
          );
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit(
            'UPDATE_INVENTORY_ADVANCE_PAYMENT',
            inventoryAdvancePayment
          );
        }
      });
  },

  assignInventoryAdvancePaymentAPSpecialist(
    context: InventoryAdvancePaymentContext,
    payload: IInventoryAdvancePaymentAPSpecialistActionPayload
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .assignInventoryAdvancePaymentAPSpecialist(
        payload.inventoryAdvancePaymentId,
        payload.apSpecialist
      )
      .then((inventoryAdvancePayment: IInventoryAdvancePaymentData) => {
        const index = context.state.inventoryAdvancePayments.findIndex(
          (v: IInventoryAdvancePaymentData) =>
            v.id === inventoryAdvancePayment.id
        );
        if (index === -1) {
          context.commit(
            'ADD_INVENTORY_ADVANCE_PAYMENT',
            inventoryAdvancePayment
          );
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit(
            'UPDATE_INVENTORY_ADVANCE_PAYMENT',
            inventoryAdvancePayment
          );
        }
      });
  },

  fetchInventoryAdvancePaymentApprovers(
    context: InventoryAdvancePaymentContext,
    payload: number
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .fetchInventoryAdvancePaymentApprovers(payload)
      .then((approvers: IUserData[]) => {
        context.commit('SET_INVENTORY_ADVANCE_PAYMENT_APPROVERS', approvers);
      });
  },

  assignInventoryAdvancePaymentApprover(
    context: InventoryAdvancePaymentContext,
    payload: IInventoryAdvancePaymentApproverActionPayload
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .assignInventoryAdvancePaymentApprover(
        payload.inventoryAdvancePaymentId,
        payload.approver
      )
      .then((inventoryAdvancePayment: IInventoryAdvancePaymentData) => {
        const index = context.state.inventoryAdvancePayments.findIndex(
          (v: IInventoryAdvancePaymentData) =>
            v.id === inventoryAdvancePayment.id
        );
        if (index === -1) {
          context.commit(
            'ADD_INVENTORY_ADVANCE_PAYMENT',
            inventoryAdvancePayment
          );
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit(
            'UPDATE_INVENTORY_ADVANCE_PAYMENT',
            inventoryAdvancePayment
          );
        }
      });
  },

  updateInventoryAdvancePaymentOwner(
    context: InventoryAdvancePaymentContext,
    payload: IInventoryAdvancePaymentOwnerActionPayload
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .updateInventoryAdvancePaymentOwner(
        payload.inventoryAdvancePaymentId,
        payload.owner
      )
      .then((inventoryAdvancePayment: IInventoryAdvancePaymentData) => {
        const index = context.state.inventoryAdvancePayments.findIndex(
          (v: IInventoryAdvancePaymentData) =>
            v.id === inventoryAdvancePayment.id
        );
        if (index === -1) {
          context.commit(
            'ADD_INVENTORY_ADVANCE_PAYMENT',
            inventoryAdvancePayment
          );
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit(
            'UPDATE_INVENTORY_ADVANCE_PAYMENT',
            inventoryAdvancePayment
          );
        }
      });
  },

  fetchInventoryAdvancePaymentActivities(
    context: InventoryAdvancePaymentContext,
    payload: number
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .fetchInventoryAdvancePaymentActivities(payload)
      .then((activities: Transition[]) => {
        context.commit('SET_INVENTORY_ADVANCE_PAYMENT_ACTIVITIES', activities);
      });
  },

  fetchInventoryAdvancePaymentComments(
    context: InventoryAdvancePaymentContext,
    payload: number
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .fetchInventoryAdvancePaymentComments(payload)
      .then((comments: ITransition[]) => {
        context.commit('SET_INVENTORY_ADVANCE_PAYMENT_COMMENTS', comments);
      });
  },

  fetchInventoryAdvancePaymentComment(
    context: InventoryAdvancePaymentContext,
    payload: IInventoryAdvancePaymentCommentFetchActionPayload
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .fetchInventoryAdvancePaymentComment(
        payload.inventoryAdvancePaymentId,
        payload.commentId
      )
      .then((comment: IInventoryAdvancePaymentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === comment.id
        );
        if (index === -1) {
          context.commit('ADD_INVENTORY_ADVANCE_PAYMENT_COMMENT', comment);
        } else {
          context.commit('UPDATE_INVENTORY_ADVANCE_PAYMENT_COMMENT', comment);
        }
      });
  },

  createInventoryAdvancePaymentComment(
    context: InventoryAdvancePaymentContext,
    payload: IInventoryAdvancePaymentCommentUpdateActionPayload
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .createInventoryAdvancePaymentComment(
        payload.inventoryAdvancePaymentId,
        payload.comment
      )
      .then((comment: IInventoryAdvancePaymentData) => {
        context.commit('ADD_INVENTORY_ADVANCE_PAYMENT_COMMENT', comment);
      });
  },
  updateInventoryAdvancePaymentComment(
    context: InventoryAdvancePaymentContext,
    payload: IInventoryAdvancePaymentCommentUpdateActionPayload
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .updateInventoryAdvancePaymentComment(
        payload.inventoryAdvancePaymentId,
        payload.comment
      )
      .then((comment: IInventoryAdvancePaymentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === comment.id
        );
        if (index === -1) {
          context.commit('ADD_INVENTORY_ADVANCE_PAYMENT_COMMENT', comment);
        } else {
          context.commit('UPDATE_INVENTORY_ADVANCE_PAYMENT_COMMENT', comment);
        }
      });
  },
  deleteInventoryAdvancePaymentComment(
    context: InventoryAdvancePaymentContext,
    payload: IInventoryAdvancePaymentCommentFetchActionPayload
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .deleteInventoryAdvancePaymentComment(
        payload.inventoryAdvancePaymentId,
        payload.commentId
      )
      .then(() => {
        context.commit(
          'REMOVE_INVENTORY_ADVANCE_PAYMENT_COMMENT',
          payload.commentId
        );
      });
  },

  addInventoryAdvancePaymentCommentAttachments(
    context: InventoryAdvancePaymentContext,
    payload: IInventoryAdvancePaymentCommentAttachmentAddActionPayload
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .addInventoryAdvancePaymentCommentAttachments(
        payload.inventoryAdvancePaymentId,
        payload.commentId,
        payload.attachments
      )
      .then((comment: IMentionAttachmentCommentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === payload.commentId
        );
        if (index === -1) {
          context.commit('ADD_INVENTORY_ADVANCE_PAYMENT_COMMENT', comment);
        } else {
          context.commit('UPDATE_INVENTORY_ADVANCE_PAYMENT_COMMENT', comment);
        }
      });
  },

  removeInventoryAdvancePaymentCommentAttachment(
    context: InventoryAdvancePaymentContext,
    payload: IInventoryAdvancePaymentCommentAttachmentRemoveActionPayload
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .removeInventoryAdvancePaymentCommentAttachment(
        payload.inventoryAdvancePaymentId,
        payload.commentId,
        payload.attachmentId
      )
      .then(() => {
        const index = context.state.comments.findIndex(
          comment => comment.id === payload.commentId
        );

        if (index !== -1) {
          const comment = context.state.comments[index];

          const attachmentIndex = comment.attachments!.findIndex(
            attachment => attachment.id === payload.attachmentId
          );

          comment.attachments!.splice(attachmentIndex, 1);

          context.commit('UPDATE_INVENTORY_ADVANCE_PAYMENT_COMMENT', comment);
        }
      });
  },

  // Mentions
  fetchInventoryAdvancePaymentCommentsMentionableUsers(
    context: InventoryAdvancePaymentContext,
    payload: number
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment
      .fetchInventoryAdvancePaymentCommentsMentionableUsers(payload)
      .then((mentionableUsers: IUserData[]) => {
        context.commit('SET_MENTIONABLE_USERS', mentionableUsers);
      });
  },

  //export
  exportInventoryAdvancePaymentPdf(
    _context: InventoryAdvancePaymentContext,
    payload: number
  ): Promise<Blob> {
    return this.$services.inventoryAdvancePayment.exportInventoryAdvancePaymentPdf(
      payload
    );
  },

  exportInventoryAdvancePayments(
    _context: InventoryAdvancePaymentContext,
    payload?: IInventoryAdvancePaymentExportFilterParams
  ): Promise<Blob> {
    return this.$services.inventoryAdvancePayment.exportInventoryAdvancePayments(
      payload
    );
  },

  updateMultipleInventoryAdvancePaymentOwner(
    _context: InventoryAdvancePaymentContext,
    payload: IInventoryAdvancePaymentOwnerMultipleUpdateActionPayload
  ): Promise<void> {
    return this.$services.inventoryAdvancePayment.updateMultipleInventoryAdvancePaymentOwner(
      payload.owner,
      payload.requisitions
    );
  }
};
